import { useEffect, useMemo, useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { Gamepad2 } from 'lucide-react'
import type { ReviewNoteView } from '@/api/lcReviewNote'
import type { LessonProcessDetailView, LessonSelectionMeta, WorkbookMode } from '@/types/coaching'
import { useAuthStore } from '@/store/authStore'
import { hasLcPermission } from '@/utils/roleUtils'
import { useFocusUnit } from '@/hooks/coaching/useFocusUnit'
import { useCoachingGuide } from '@/hooks/coaching/useCoachingGuide'
import { useWorkbookGrades } from '@/hooks/coaching/useWorkbookGrades'
import { useReviewNote } from '@/hooks/coaching/useReviewNote'
import { usePersistedCoachingPanelCollapse } from '@/hooks/coaching/usePersistedCoachingPanelCollapse'
import { usePersistedGradingMode } from '@/hooks/coaching/usePersistedGradingMode'
import type { CoachingFocusRequest } from './grading-sheet/coachingFocus'
import { UnitListPanel } from './UnitListPanel'
import { UnitDetailPanel } from './UnitDetailPanel'
import { ReviewNotePanel } from './ReviewNotePanel'
import { ReviewToggle } from './ReviewToggle'
import { WorkbookGradingPanel } from './WorkbookGradingSection'
import { SubjectInfoBody } from './CoachingGuidePanel'
import { UnderstandingCheckPanel } from './UnderstandingCheckPanel'
import { LessonUnderstandingSummary } from './LessonUnderstandingSummary'
import { LessonPhotoPanel } from './lesson-photos/LessonPhotoPanel'
import type { OperationStep, OperationStepKey } from './OperationStepListItem'
import { understandingStepStatus, workbookStepStatus } from './lessonOperationStatus'
import { openGameMathPage } from './gameMathNavigation'

interface Props {
  learnerId: number
  lesson: LessonProcessDetailView
  selectionMeta?: LessonSelectionMeta | null
  readOnly?: boolean
  lessonActions?: React.ReactNode
  workbookMode?: WorkbookMode
  onWorkbookModeChange?: (mode: WorkbookMode) => void
  attemptScope?: 'all' | 'progress' | 'review'
  photoReadOnly?: boolean
}

function defaultAttemptView(
  lesson: LessonProcessDetailView,
  attemptScope: Props['attemptScope'],
): 'progress' | 'review' {
  if (attemptScope === 'progress') return 'progress'
  if (attemptScope === 'review') return 'review'
  return lesson.lessonKind === 'designated_review' && lesson.status !== 'completed'
    ? 'review'
    : 'progress'
}

function reviewNoteOperationStep(
  reviewNote: ReviewNoteView | null | undefined,
  t: ReturnType<typeof useTranslation>['t'],
): OperationStep {
  switch (reviewNote?.status) {
    case 'EMPTY':
      return { key: 'reviewNote', status: 'not_started', statusLabel: t('reviewNote.listStatus.empty') }
    case 'GENERATED':
      return {
        key: 'reviewNote',
        status: 'in_progress',
        statusLabel: t('reviewNote.listStatus.generated'),
        needsGrading: true,
      }
    case 'IN_PROGRESS':
      return {
        key: 'reviewNote',
        status: 'in_progress',
        statusLabel: t('reviewNote.listStatus.inProgress', {
          graded: reviewNote.gradedCount,
          total: reviewNote.totalCount,
        }),
        needsGrading: true,
      }
    case 'COMPLETED':
      return {
        key: 'reviewNote',
        status: 'completed',
        statusLabel: t('unit.list.status.completed'),
      }
    case 'CANCELLED':
      return { key: 'reviewNote', status: 'not_started', statusLabel: t('reviewNote.listStatus.cancelled') }
    default:
      return { key: 'reviewNote', status: 'not_started', statusLabel: t('reviewNote.listStatus.notStarted') }
  }
}

/**
 * 단일(오늘 세션) 레슨 채점 뷰 — 콘텐츠 트리에서 선택된 레슨을 채점한다.
 * 레슨 전환은 트리가 담당하고, 교과 정보·이해도·워크북 운영은 이 화면에 합성한다.
 */
export function LessonGradingPane({
  learnerId,
  lesson,
  selectionMeta,
  readOnly = false,
  lessonActions,
  workbookMode = 'IMMEDIATE',
  onWorkbookModeChange,
  attemptScope = 'all',
  photoReadOnly = readOnly,
}: Props) {
  const { t } = useTranslation('coaching')
  const processListPanel = usePersistedCoachingPanelCollapse('processListCollapsed')
  const user = useAuthStore((s) => s.user)
  const canEdit = !readOnly && hasLcPermission(user ?? undefined, 'COACHING')
  const hasReviewAttempt = lesson.lessonKind === 'designated_review' || Boolean(lesson.reviewComponents?.length)
  const [attemptView, setAttemptView] = useState<'progress' | 'review'>(() => defaultAttemptView(lesson, attemptScope))
  const [selectedOperationStep, setSelectedOperationStep] = useState<OperationStepKey | null>(null)
  // 채점/코칭 모드(기본 채점, 교사별 기억) + 채점표 [답안 편집] → 코칭모드 문항 선택 요청.
  const { mode: gradingMode, setMode: setGradingMode } = usePersistedGradingMode()
  const [focusRequest, setFocusRequest] = useState<CoachingFocusRequest | null>(null)

  useEffect(() => {
    setAttemptView(defaultAttemptView(lesson, attemptScope))
    setSelectedOperationStep(null)
  }, [attemptScope, lesson.lessonId, lesson.lessonKind, lesson.reviewComponents, lesson.status])

  useEffect(() => {
    setFocusRequest(null)
  }, [lesson.lessonId, attemptView])

  const requestCoaching = (scope: CoachingFocusRequest['scope'], quizId: string) => {
    setGradingMode('coaching')
    setFocusRequest({ scope, quizId, nonce: Date.now() })
  }
  const pauseFocus = focusRequest?.scope === 'pause' ? focusRequest : null
  const reviewNoteFocus = focusRequest?.scope === 'reviewNote' ? focusRequest : null

  const selectedComponents = attemptView === 'review'
    ? (lesson.reviewComponents?.length ? lesson.reviewComponents : (lesson.status === 'not_started' ? [] : lesson.components))
    : lesson.components
  const showReviewNotStarted = attemptView === 'review' &&
    lesson.lessonKind === 'designated_review' &&
    lesson.status === 'not_started' &&
    selectedComponents.length === 0
  const selectedLesson = useMemo(
    () => ({ ...lesson, components: selectedComponents }),
    [lesson, selectedComponents],
  )
  const selectedCanEdit = canEdit && attemptView === 'progress'
  const { focusUnitId, setFocusUnitId } = useFocusUnit(selectedLesson)
  const focusUnit = useMemo(
    () => selectedComponents.find((c) => c.componentProgressId === focusUnitId) ?? null,
    [selectedComponents, focusUnitId],
  )
  const learningAttemptId = attemptView === 'review' ? (lesson.reviewAttemptId ?? null) : null
  const hasAttemptScope = attemptView === 'progress' || learningAttemptId != null
  const guide = useCoachingGuide(learnerId, lesson.lessonId, learningAttemptId, hasAttemptScope && !showReviewNotStarted)
  const reviewNote = useReviewNote(learnerId, lesson.lessonId, learningAttemptId, hasAttemptScope)
  const showWorkbookStep = attemptView === 'progress' && lesson.workbookGradingEnabled !== false
  const workbook = useWorkbookGrades(learnerId, lesson.lessonId, showWorkbookStep && !showReviewNotStarted)
  const understandingStatus = understandingStepStatus(guide.data?.essentialChecks)
  const workbookStatus = workbookStepStatus(workbook.data)
  const operationSteps: OperationStep[] = [
    reviewNoteOperationStep(reviewNote.data, t),
    {
      key: 'understanding',
      status: understandingStatus,
      needsGrading: understandingStatus === 'in_progress',
    },
  ]
  if (showWorkbookStep) {
    operationSteps.push({
      key: 'workbook',
      status: workbookStatus,
      needsGrading: workbookStatus === 'in_progress',
    })
  }
  if (hasAttemptScope) operationSteps.push({ key: 'lessonPhoto', status: 'not_started', statusLabel: t('lessonPhotos.view') })

  useEffect(() => {
    if (selectedOperationStep === 'workbook' && !showWorkbookStep) {
      setSelectedOperationStep('understanding')
    } else if (focusUnitId == null && selectedOperationStep == null) {
      setSelectedOperationStep('understanding')
    }
  }, [focusUnitId, selectedOperationStep, showWorkbookStep])
  const gameKeys = selectionMeta?.gameKeys.length ? selectionMeta.gameKeys : (lesson.gameKeys ?? [])
  const lessonGameKey = gameKeys[0]
  const chapterNumber = selectionMeta?.chapterNumber ?? lesson.chapterLabel
  const chapterTitle = selectionMeta?.chapterTitle ?? lesson.moduleTitle
  const lessonNumber = selectionMeta?.lessonNumber ?? '-'
  const chapterPrefix = chapterNumber === '-' ? '-' : `챕터${chapterNumber}.`
  const lessonPrefix = lessonNumber === '-' ? '-' : `${lessonNumber}.`
  const lessonKindLabel = attemptView === 'review'
    ? t('contentManagement.lessonKind.review')
    : t('contentManagement.lessonKind.progress')
  const lessonCompleted = attemptView === 'review'
    ? lesson.reviewAttemptCompleted === true
    : lesson.status === 'completed'
  const lessonMaterials = lesson.materials ?? []
  const workbookMaterial = lessonMaterials.find(
    (material) => material.materialType === 'WORKBOOK',
  )
  const secondaryLessonActions = !readOnly && lessonGameKey ? (
    <button
      type="button"
      onClick={() => openGameMathPage(lessonGameKey)}
      className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-sm font-semibold text-cyan-700 hover:bg-cyan-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-1"
    >
      <Gamepad2 size={14} /> {t('library.gameMathTag')}
    </button>
  ) : null

  return (
    <section aria-label={t('progress.title')} className="flex flex-col gap-3">
      <div className="rounded-lg border border-gray-200 bg-white p-4" data-testid="content-body">
        <div
          className="mb-4 grid grid-cols-1 items-start gap-3 border-b border-gray-100 pb-3 xl:grid-cols-[minmax(180px,1fr)_minmax(300px,360px)_auto]"
          data-testid="lesson-heading"
        >
          <div className="min-w-0 space-y-1.5" data-testid="lesson-title-block">
            <h2
              className="flex min-w-0 items-baseline gap-1.5 text-sm font-semibold text-gray-700"
              data-testid="chapter-info-row"
            >
              <span className="shrink-0 text-gray-500">{chapterPrefix}</span>
              {' '}
              <span className="truncate">
                {chapterTitle}
              </span>
            </h2>
            <div
              className="flex min-w-0 flex-wrap items-center gap-2 text-base font-semibold text-gray-950"
              data-testid="lesson-info-row"
            >
              <span className="min-w-0 truncate">
                <span className="text-gray-500">{lessonPrefix}</span> <ContentMetadataText text={lesson.lessonTitle} />
              </span>
              <span className="inline-flex shrink-0 items-center rounded-full border border-lc-mint-200 bg-lc-mint-100 px-2.5 py-0.5 text-xs font-semibold text-lc-mint-900">
                {lessonKindLabel}
              </span>
            </div>
            {lessonMaterials.length > 0 ? (
              <ul
                className="flex flex-col gap-1 text-xs text-gray-600"
                aria-label={t('lessonMaterial.aria')}
                data-testid="lesson-materials"
              >
                {lessonMaterials.map((material) => (
                  <li key={`${material.bookId}-${material.materialType}`}>
                    <span className="font-semibold text-gray-700">
                      {material.startPage != null
                        ? t('lessonMaterial.bookWithPage', {
                            bookName: material.title,
                            page: material.startPage,
                          })
                        : material.title}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            {guide.data?.subjectInfo ? (
              <div data-testid="lesson-subject-info" className="text-gray-600">
                <SubjectInfoBody info={guide.data.subjectInfo} />
              </div>
            ) : null}
          </div>
          <LessonUnderstandingSummary
            lessonCompleted={lessonCompleted}
            understanding={guide.data?.understanding}
            isLoading={guide.isLoading}
          />
          <div className="flex flex-wrap items-start justify-end gap-2" data-testid="lesson-action-row">
            {!readOnly && attemptView === 'progress' && lesson.status === 'completed' && (
              <ReviewToggle learnerId={learnerId} lessonId={lesson.lessonId} lessonTitle={lesson.lessonTitle} />
            )}
            {lessonActions}
          </div>
        </div>

        {attemptScope === 'all' && hasReviewAttempt && (
          <div className="mb-4 inline-flex rounded border border-gray-200 bg-gray-50 p-1" aria-label={t('reviewAttempt.switchLabel')}>
            <button
              type="button"
              onClick={() => setAttemptView('progress')}
              aria-pressed={attemptView === 'progress'}
              className={clsx(
                'rounded px-3 py-1.5 text-xs font-semibold transition',
                attemptView === 'progress' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900',
              )}
            >
              {t('reviewAttempt.progressTab')}
            </button>
            <button
              type="button"
              onClick={() => setAttemptView('review')}
              aria-pressed={attemptView === 'review'}
              className={clsx(
                'rounded px-3 py-1.5 text-xs font-semibold transition',
                attemptView === 'review' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900',
              )}
            >
              {t('reviewAttempt.reviewTab', { n: lesson.reviewAttemptNumber ?? 1 })}
            </button>
          </div>
        )}

        {!showReviewNotStarted && secondaryLessonActions && (
          <div className="mb-4 flex flex-wrap items-center justify-end gap-2 border-b border-gray-100 pb-3" data-testid="workbook-action-row">
            {secondaryLessonActions}
          </div>
        )}

        <div data-process-list-collapsed={processListPanel.collapsed} className={processListPanel.collapsed
          ? 'grid grid-cols-[56px_minmax(0,1fr)] gap-4'
          : 'grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]'}>
          <UnitListPanel
            components={selectedComponents}
            focusUnitId={focusUnitId}
            gameKeys={gameKeys}
            collapsed={processListPanel.collapsed}
            onToggleCollapsed={processListPanel.toggle}
            onSelect={(componentProgressId) => {
              setSelectedOperationStep(null)
              setFocusUnitId(componentProgressId)
            }}
            operationSteps={operationSteps}
            selectedOperationStep={selectedOperationStep}
            onSelectOperation={(key) => {
              setFocusUnitId(null)
              setSelectedOperationStep(key)
            }}
          />
          {selectedOperationStep === 'lessonPhoto' && hasAttemptScope ? (
            <LessonPhotoPanel key={`${learnerId}:${lesson.lessonId}:${learningAttemptId ?? 'progress'}`} learnerId={learnerId} lessonId={lesson.lessonId} attemptId={learningAttemptId} readOnly={photoReadOnly} />
          ) : selectedOperationStep === 'reviewNote' ? (
            <ReviewNotePanel
              learnerId={learnerId}
              lessonId={lesson.lessonId}
              learningAttemptId={learningAttemptId}
              hasAttemptScope={hasAttemptScope}
              readOnly={readOnly}
              gradingMode={gradingMode}
              onGradingModeChange={setGradingMode}
              onRequestCoaching={(quizId) => requestCoaching('reviewNote', quizId)}
              focusRequest={reviewNoteFocus}
            />
          ) : selectedOperationStep === 'understanding' ? (
            <UnderstandingCheckPanel
              learnerId={learnerId}
              lessonId={lesson.lessonId}
              learningAttemptId={learningAttemptId}
              guide={guide.data}
              isLoading={guide.isLoading}
              canEdit={canEdit && hasAttemptScope}
            />
          ) : selectedOperationStep === 'workbook' ? (
            <WorkbookGradingPanel
              learnerId={learnerId}
              lessonId={lesson.lessonId}
              canEdit={canEdit}
              workbookMode={workbookMode}
              onWorkbookModeChange={onWorkbookModeChange}
              bookTitle={workbookMaterial?.title}
              startPage={workbookMaterial?.startPage}
            />
          ) : (
            <UnitDetailPanel
              component={focusUnit}
              learnerId={learnerId}
              lessonId={lesson.lessonId}
              gameKeys={gameKeys}
              canEdit={selectedCanEdit}
              gradingMode={gradingMode}
              onGradingModeChange={setGradingMode}
              onRequestCoaching={(quizId) => requestCoaching('pause', quizId)}
              initialSelectedQuizId={pauseFocus?.quizId ?? null}
              focusRequest={pauseFocus}
            />
          )}
        </div>
      </div>
    </section>
  )
}
